import { useCallback, useEffect, useRef, useState } from "react";

import { requestVideoUploadUrl } from "../api/requestVideoUploadUrl";
import { putFileWithProgress } from "../utils/putFileWithProgress";

/**
 * Presign + PUT a video, with progress, cancel and a busy signal for the form
 * that hosts it.
 *
 * `upload(file)` resolves with `final_url` only after the PUT returned 2xx, so
 * the host saves a URL that already exists (captions requested on save read the
 * file right away). An upload still running when the component unmounts is
 * aborted.
 *
 * @param {{ onUploadingChange?: (uploading: boolean) => void }} [options]
 *        called with `true` when an upload starts and `false` when it settles
 *        (success, failure or cancel) — wire it to the host's Save button
 * @returns {{
 *   upload: (file: File) => Promise<string>,
 *   cancel: () => void,
 *   status: null | { name: string, percent: number, secondsLeft: number|null, finishing: boolean },
 *   isUploading: boolean,
 * }}
 */
export function useVideoUpload({ onUploadingChange } = {}) {
    const [status, setStatus] = useState(null);
    const controllerRef = useRef(null);

    const onUploadingChangeRef = useRef(onUploadingChange);
    onUploadingChangeRef.current = onUploadingChange;

    const cancel = useCallback(() => {
        controllerRef.current?.abort();
    }, []);

    const upload = useCallback(async (file) => {
        // One upload at a time: a new file replaces the one in flight.
        controllerRef.current?.abort();
        const controller = new AbortController();
        controllerRef.current = controller;

        setStatus({ name: file.name, percent: 0, secondsLeft: null, finishing: false });
        onUploadingChangeRef.current?.(true);

        try {
            const { upload_url, final_url } = await requestVideoUploadUrl(file);
            const startedAt = Date.now();

            await putFileWithProgress(upload_url, file, {
                contentType: file.type,
                signal: controller.signal,
                onProgress: ({ loaded, total }) => {
                    if (controller.signal.aborted) return;
                    const percent = Math.round((loaded * 100) / total);
                    const speed = loaded / ((Date.now() - startedAt) / 1000);
                    setStatus({
                        name: file.name,
                        percent,
                        secondsLeft: speed > 0 ? (total - loaded) / speed : null,
                        // Every byte is out, but S3 has not answered yet.
                        finishing: percent >= 100,
                    });
                },
            });

            if (controller.signal.aborted) {
                throw new DOMException("Upload aborted", "AbortError");
            }

            return final_url;
        } finally {
            if (controllerRef.current === controller) {
                controllerRef.current = null;
                setStatus(null);
            }
            onUploadingChangeRef.current?.(false);
        }
    }, []);

    useEffect(() => () => controllerRef.current?.abort(), []);

    return { upload, cancel, status, isUploading: !!status };
}

/**
 * @param {unknown} error
 * @returns {boolean} whether `error` is the cancel of an upload
 */
export function isUploadAborted(error) {
    return error?.name === "AbortError";
}
